import { Link } from "react-router-dom";
import { Phone, ArrowRight, FileText, ClipboardCheck, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import TrustBar from "@/components/TrustBar";
import SectionHeading from "@/components/SectionHeading";
import ServiceCard from "@/components/ServiceCard";
import QuoteForm from "@/components/QuoteForm";
import { business, homeServices, images } from "@/data/site";

const KEYWORDS =
  "Roofing company Bucks County PA, Roofer Bristol PA, Roof repair Bucks County, Residential roofing Bucks County, Commercial roofing Bucks County, Flat roofing contractor, Gutter installation Bucks County, Siding installation Bucks County, Licensed and insured roofer, Family-owned roofing company";

const homeSchema = {
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  name: business.name,
  telephone: "+1-215-806-5038",
  email: business.email,
  areaServed: "Bucks County, Pennsylvania",
  address: {
    "@type": "PostalAddress",
    streetAddress: business.address.street,
    addressLocality: business.address.city,
    addressRegion: business.address.state,
    postalCode: business.address.zip,
    addressCountry: "US",
  },
};

const featuredTitles = [
  "Roof Repair",
  "Residential Roofing",
  "Commercial Roofing",
  "Flat Roofing",
  "Metal Roofing",
  "Gutters & Siding",
];
const featuredServices = homeServices.filter((s) => featuredTitles.includes(s.title));

const highlights = [
  { icon: ClipboardCheck, title: "Free Estimates", body: "Free estimates available. Inspections may have a fee." },
  { icon: ShieldCheck, title: "Licensed & Insured", body: "Licensed, insured, and GAF certified for your peace of mind." },
  { icon: FileText, title: "Insurance Claims Help", body: "We work with insurers, help with paperwork, and meet adjusters." },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Future Roofing and Repair | Roofing Company in Bucks County, PA"
        description="Family-owned roofing company in Bucks County, PA. Residential & commercial roofing, roof repairs, gutters, siding and roof maintenance. Call 215-806-5038 for a free estimate."
        keywords={KEYWORDS}
        schema={homeSchema}
      />

      {/* HERO */}
      <section className="relative" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={images.hero} alt="Home with a new roof under a dramatic sky in Bucks County" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/72" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36">
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl uppercase text-cream leading-[0.9] max-w-3xl">
            Family-Owned Roofing &amp; Repair in Bucks County
          </h1>
          <p className="mt-6 text-base md:text-lg text-cream/85 max-w-xl leading-relaxed">
            Residential and commercial roofing backed by 13 years of hands-on experience — roof
            repairs, gutters, siding, and more.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-9">
            <a
              href={business.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 bg-rust text-cream shadow-md hover:bg-navy transition-colors font-heading text-2xl uppercase px-7 py-4"
            >
              <Phone className="w-6 h-6" /> Call {business.phone}
            </a>
            <Link
              to="/contact"
              data-testid="hero-estimate-btn"
              className="inline-flex items-center justify-center gap-2 bg-cream text-charcoal shadow-md hover:bg-cream-200 transition-colors font-heading text-2xl uppercase px-7 py-4"
            >
              Request an Estimate <ArrowRight className="w-6 h-6" />
            </Link>
          </div>
        </div>
      </section>

      <TrustBar />

      {/* SERVICES */}
      <section className="bg-cream" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <SectionHeading
            kicker="What We Do"
            title="Roofing Services"
            subtitle="We handle roofing, gutters, and siding for homes and businesses across Bucks County."
            center
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredServices.map((s) => (
              <ServiceCard key={s.title} service={s} />
            ))}
          </div>
          <p className="text-center text-charcoal/70 mt-8">
            Also offering shingle &amp; slate roofing, fascia &amp; soffit, attic ventilation, and
            maintenance.{" "}
            <Link to="/residential" className="text-rust font-medium hover:underline">
              See residential
            </Link>{" "}
            ·{" "}
            <Link to="/commercial" className="text-rust font-medium hover:underline">
              commercial
            </Link>
            .
          </p>
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-cream-200" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img
              src={images.about}
              alt="Rooftops under a dramatic sky"
              className="w-full h-[400px] object-cover shadow-md"
            />
            <div className="absolute -bottom-4 right-4 bg-rust text-cream shadow-md px-5 py-3 text-center">
              <span className="block font-heading text-4xl leading-none">13</span>
              <span className="font-accent uppercase text-xs tracking-widest">Years Experience</span>
            </div>
          </div>
          <div>
            <SectionHeading kicker="Our Story" title="A Local, Family-Owned Company" />
            <div className="space-y-4 text-charcoal/80 leading-relaxed">
              <p>
                Future Roofing and Repair is a family-owned, locally operated roofing company based in
                Bristol, PA, serving Bucks County and the surrounding areas with a straightforward,
                no-pressure approach.
              </p>
              <p>
                Owner <strong className="text-charcoal">Kyle McClinton</strong> brings 13 years of
                hands-on experience to residential and commercial roofing projects of every size.
                Licensed, insured, and GAF certified.
              </p>
            </div>
            <Link
              to="/about"
              data-testid="about-cta"
              className="mt-7 inline-flex items-center gap-2 text-rust font-accent uppercase text-sm tracking-widest hover:gap-3 transition-all"
            >
              More about us <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-cream border-y border-charcoal/10" data-testid="home-highlights">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          {highlights.map((h) => (
            <div key={h.title} className="flex items-start gap-4">
              <span className="flex items-center justify-center w-11 h-11 shrink-0 bg-cream-200 text-rust">
                <h.icon className="w-5 h-5" />
              </span>
              <div>
                <h3 className="font-heading text-xl uppercase text-charcoal leading-none">{h.title}</h3>
                <p className="mt-1.5 text-sm text-charcoal/70 leading-relaxed">{h.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="bg-charcoal" data-testid="home-quote">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionHeading kicker="Free Estimate" title="Request Your Free Estimate" light />
            <p className="text-cream/80 leading-relaxed">
              Tell us about your project and we'll get back to you quickly. Prefer to talk it through?
              Give us a call.
            </p>
            <a
              href={business.phoneHref}
              className="mt-6 inline-flex items-center gap-2 text-cream font-heading text-3xl uppercase hover:text-rust transition-colors"
            >
              <Phone className="w-7 h-7" /> {business.phone}
            </a>
            <ul className="mt-8 space-y-2.5 text-cream/75 text-sm">
              <li>Serving {business.serviceArea}</li>
              <li>Hours: {business.hours}</li>
              <li>Ask about senior, military, veteran, first responder &amp; teacher discounts</li>
            </ul>
          </div>
          <QuoteForm />
        </div>
      </section>
    </>
  );
}
